import { defineComponent as _defineComponent } from 'vue'
import { CalendarIcon } from '@heroicons/vue/solid';
import { toast, toastServiceError } from '@/services';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { WorkScheduleModel } from '@/types/models/WorkScheduleModel';
import { WorkScheduleGetAllParams } from '@/types/services/WorkScheduleService';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    FilterItem,
    LevelFilter,
    Permission,
    SortMenuItem,
    WorkScheduleActions,
    WorkScheduleGetters,
} from '@/types';
import {
    WorkScheduleListFilters,
    WorkScheduleListItem,
    WorkScheduleListSort,
} from '@/views/pages/work-schedule/components';

const perPage = 10;

// Computed

export default /*@__PURE__*/_defineComponent({
  __name: 'WorkSchedulePage',
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const filterStore = useFilterStore();

const workSchedules = ref<WorkScheduleModel[]>([]);
const count = ref(0);
const search = computed({
    get: () => store.getters[WorkScheduleGetters.SEARCH],
    set: (value: string) => store.dispatch(WorkScheduleActions.SET_SEARCH, value),
});
const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[WorkScheduleGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(WorkScheduleActions.SET_SORT, value),
});
const currentPage = computed({
    get: () => store.getters[WorkScheduleGetters.PAGE],
    set: (value: number) => store.dispatch(WorkScheduleActions.SET_PAGE, value),
});

const filter: LevelFilter = store.getters[WorkScheduleGetters.FILTER];
const activeFilters = computed<FilterItem[]>(
    () => store.getters[WorkScheduleGetters.ACTIVE_FILTERS],
);
const levelFilter = computed(() => filterStore.level);
const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getQueryParams = (): WorkScheduleGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchAllWorkSchedules = async () => {
    try {
        setLoading(true);
        const { data, headers } = await WorkScheduleService.getAll<WorkScheduleModel[]>({
            params: getQueryParams(),
        });
        workSchedules.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const updateWorkScheduleStatus = async (workSchedule: WorkScheduleModel, isActive: boolean) => {
    try {
        setLoading(true);
        const { data } = await (isActive
            ? WorkScheduleService.activate(workSchedule.id)
            : WorkScheduleService.inactivate(workSchedule.id));
        toast.success(data.message);
        await fetchAllWorkSchedules();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleActivateClick = (item: WorkScheduleModel) => updateWorkScheduleStatus(item, true);
const handleInactivateClick = (item: WorkScheduleModel) => updateWorkScheduleStatus(item, false);

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};
const handleRemoveFilterClick = (item: FilterItem) =>
    store.dispatch(WorkScheduleActions.CLEAR_FILTER, item);

// Watchers
watch([currentPage], fetchAllWorkSchedules);
watch([levelFilter, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchAllWorkSchedules();
    else currentPage.value = 1;
});

onMounted(() => {
    if (route.query.clearStateOnSetup) {
        search.value = '';
        sortMenuSelected.value = [];
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    fetchAllWorkSchedules();
});

const __returned__ = { route, router, uiStore, filterStore, workSchedules, count, perPage, search, sortMenuSelected, currentPage, filter, activeFilters, levelFilter, orderBy, setLoading, getQueryParams, fetchAllWorkSchedules, updateWorkScheduleStatus, handleActivateClick, handleInactivateClick, handleRemoveSortClick, handleRemoveFilterClick, get CalendarIcon() { return CalendarIcon }, get Permission() { return Permission }, get WorkScheduleListFilters() { return WorkScheduleListFilters }, get WorkScheduleListItem() { return WorkScheduleListItem }, get WorkScheduleListSort() { return WorkScheduleListSort } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})